import React from "react";
import { Box, Tooltip, Typography } from "@material-ui/core";
import { css } from "@emotion/css";
import { useFormContext } from "react-hook-form";
import ProgressButton from "shared/ProgressButton/ProgressButton";
import { LessonFormData } from "../types";
import { useLessonValidation } from "../hooks/useLessonValidation";

/**
 * Save button and validation issue list are split into leaf components so that
 * subscribing to `parts` only re-renders these leaves on each keystroke —
 * not the entire LessonEditor tree.
 */

export const LessonSaveButton: React.VFC<{ isEditing: boolean; saving: boolean }> = ({ isEditing, saving }) => {
  const form = useFormContext<LessonFormData>();
  const canSave = useLessonValidation(form).length === 0;
  return (
    <Tooltip title={canSave ? "" : "Resolve the issues below before saving"} placement="top">
      {/* span wrapper lets the tooltip fire even while the button is disabled */}
      <span>
        <ProgressButton loading={saving} disabled={!canSave || saving}>
          {isEditing ? "Save lesson" : "Create lesson"}
        </ProgressButton>
      </span>
    </Tooltip>
  );
};

export const LessonValidationIssues: React.VFC = () => {
  const form = useFormContext<LessonFormData>();
  const issues = useLessonValidation(form);
  if (issues.length === 0) return null;
  return (
    <Box className={styles.issueList}>
      <Typography variant="body2" color="error">
        Resolve {issues.length} issue{issues.length === 1 ? "" : "s"} before saving:
      </Typography>
      <ul className={styles.issueItems}>
        {issues.map((issue, i) => (
          <li key={i}>
            <Typography variant="body2" color="error">
              {issue}
            </Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
};

const styles = {
  issueList: css`
    margin-top: 12px;
    padding: 8px 12px;
    background: #fff3f0;
    border: 1px solid #f3c4bc;
    border-radius: 4px;
  `,
  issueItems: css`
    margin: 4px 0 0;
    padding-left: 20px;
  `,
};
